import { useContext, useState } from 'react'

import { Onboarding } from '@hhgtech/hhg-components/onboardingV2'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { callApiWithLead, postSSOApiFormData } from 'api/utils'
import { getLeadPath } from 'api/utils/paths'
import { showNotification } from 'components/molecules/notificationsHandler'
import ErrorBoundary from 'components/templates/navigatorLayout/errorBoundary'
import { useIsMobile } from 'hooks/useIsMobile'
import { DISPLAY_DATE_FORMAT } from 'interfaces/constants'
import { Information } from 'interfaces/types/DueDate'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'
import { useHealthToolStore } from 'state/healthTool'

import { SimilacDefaultBanner } from './similacDefaultBanner'

export type Props = {
  isSimilac?: boolean
  isSkippable?: boolean
  leadCampaignCode?: string
  termContent?: string
  thanksPageInfo?: Information
  bannerMobile?: string
  bannerDesktop?: string
  incentiveMessage?: Information
  onClose?: () => void
}

const SimilacOnboardingModal = ({
  isSkippable,
  leadCampaignCode,
  termContent,
  bannerMobile,
  bannerDesktop,
  thanksPageInfo,
  incentiveMessage,
  onClose = () => false,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const [step, setStep] = useState(0)
  const { resultBabyDOB, resultDueDate } = useHealthToolStore(
    (state) => state,
    shallow,
  )
  const [onBoardingFormValues, setOnBoardingFormValues] = useState<any>()

  const {
    state: { userInfo },
    dispatch,
  } = useContext(AppContext)

  const onFormSubmit = async (values?: any) => {
    const phone = values?.phone

    if (!phone) return
    setOnBoardingFormValues(values)
    try {
      const response = await postSSOApiFormData(
        PATHS.LEADGEN.POST_LEAD_SEND_OTP,
        {
          phone_number: phone.startsWith('+') ? phone : `+${phone}`,
        },
      )

      /**
        - This phone number was verified
        - Send sms successfully
        - Send sms Fail
        - SMS OTP not support your country
        */
      const respMessage = response?._messages?.[0]

      if (
        respMessage === 'SMS OTP not support your country' ||
        respMessage === 'Send sms Fail'
      ) {
        const defaultError = f({ id: 'notification.comment.somethingWrong' })
        showNotification({
          title: respMessage || defaultError,
          message: '',
          type: 'danger',
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onResendCode = async (phone: string) => {
    await postSSOApiFormData(PATHS.LEADGEN.POST_LEAD_SEND_OTP, {
      phone_number: phone.startsWith('+') ? phone : `+${phone}`,
    })
  }
  const [otpError, setOtpError] = useState<string>()
  const onOtpSubmit = async (otp: string) => {
    // return {}
    setOtpError(undefined)
    try {
      const res = await postSSOApiFormData(
        PATHS.SUBOT.POST_VERIFY_SMS_OTP,
        {
          otp_code: otp,
        },
        { method: 'POST' },
      )
      // - Wrong Phone Number
      // - This phone number was verified
      // - Verify successfully
      // - Verify OTP failed
      // - SMS OTP not support your country
      const respMessage = res?._messages[0]
      if (respMessage === 'Verify sucess') {
        if (leadCampaignCode) {
          const {
            address,
            email,
            name,
            phone,
            // term,
          } = onBoardingFormValues
          callApiNew(
            PATHS.SSO.UPDATE_PROFILE,
            {
              method: 'POST',
              data: {
                name,
              },
            },
            'sso',
          )
          const res = await callApiWithLead(
            getLeadPath(PATHS.LEADGEN.POST_LEAD_LEAD, {
              campaignCode: leadCampaignCode,
            }),
            'POST',
            {
              data: {
                url: window.location.href,
                title_article: document.title,
                referrer: window.location.href,
                meta: [
                  {
                    key: 'name',
                    value: name,
                    control: 'text',
                  },
                  {
                    key: 'email',
                    value: email || userInfo?.email || '',
                    control: 'text',
                  },
                  {
                    key: 'phone',
                    value: phone,
                    control: 'text',
                  },
                  {
                    key: 'address',
                    value: [
                      address?.selectedProvince || '',
                      address?.selectedDistrict || '',
                      address?.selectedWard || '',
                      address?.extraInfo || '',
                    ].join('; '),
                    control: 'text',
                  },
                  ...(resultBabyDOB
                    ? [
                        {
                          key: 'baby_dob',
                          value: dayjs(resultBabyDOB).format(
                            DISPLAY_DATE_FORMAT,
                          ),
                          control: 'text',
                        },
                      ]
                    : []),
                  ...(resultDueDate
                    ? [
                        {
                          key: 'baby_due_date',
                          value: dayjs(resultDueDate).format(
                            DISPLAY_DATE_FORMAT,
                          ),
                          control: 'text',
                        },
                      ]
                    : []),
                ],
              },
            },
          )
          if (res?._status !== 1) {
            console.error("Fail to submit leadgen's form")
          }
        }

        showNotification({
          title: f({
            id: 'subot.verifyOTP.success.message',
            defaultMessage: 'Verified successful',
          }),
          message: '',
          type: 'success',
        })
        const user = await callApiNew(
          PATHS.USER_PROFILE.GET_PROFILE,
          {
            method: 'POST',
          },
          'sso',
        )
        if (user?._data?.id)
          dispatch({
            type: actionTypes.LOGIN,
            payload: user?._data,
          })
        // onClose()
      } else if (respMessage === 'This phone number was verified') {
        showNotification({
          title: 'This phone number was verified',
          message: '',
          type: 'success',
        })
        const user = await callApiNew(
          PATHS.USER_PROFILE.GET_PROFILE,
          {
            method: 'POST',
          },
          'sso',
        )
        if (user?._data?.id)
          dispatch({
            type: actionTypes.LOGIN,
            payload: user?._data,
          })
        // onClose()
      } else {
        const defaultError = f({ id: 'notification.comment.somethingWrong' })
        const error = res?._messages[0] || defaultError
        setOtpError(error)
        return error
        // showNotification({
        //   title: res?._messages[0] || defaultError,
        //   message: '',
        //   type: 'danger',
        // })
      }
    } catch (error) {
      console.error(error)
      const defaultError = f({ id: 'notification.comment.somethingWrong' })

      showNotification({
        title: defaultError,
        message: '',
        type: 'danger',
      })
      return error

      // onClose()
    }
    return undefined
  }

  return (
    <>
      <ErrorBoundary>
        <Onboarding.Provider>
          <Onboarding.Modal
            withPortal
            withSimilac={step !== 1}
            banner={
              step !== 1
                ? {
                    imgSrc: (isMobile ? bannerMobile : bannerDesktop) || '',
                    imgComponent: <SimilacDefaultBanner />,
                  }
                : undefined
            }
          >
            <Onboarding.StepRenderer onStepChange={setStep}>
              <Onboarding.SimilacForm
                onClose={onClose}
                onSubmit={onFormSubmit}
                canSkip={isSkippable}
                customFormTexts={{
                  heading:
                    incentiveMessage?.title ||
                    f({
                      id: 'similac.onboarding.defaultBanner.title',
                    }),
                  description:
                    incentiveMessage?.description ||
                    f({
                      id: 'similac.onboarding.defaultBanner.subtitle',
                    }),
                }}
                customTerms={
                  termContent ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: termContent.replace(
                          /href="/gi,
                          'target="_blank" href="',
                        ),
                      }}
                    ></div>
                  ) : undefined
                }
                defaultValues={{
                  phone: userInfo?.phone,
                  email:
                    userInfo?.email && !userInfo.email.endsWith('@facebook.com')
                      ? userInfo.email
                      : '',
                  name: userInfo?.name,
                }}
                alwaysRender
              />
              <Onboarding.OtpForm.Phone
                onSubmit={onOtpSubmit}
                onResendCode={onResendCode}
                otpError={otpError}
                onClose={onClose}
                canSkip={isSkippable}
              />
              <Onboarding.ThankYou
                thanksPageInfo={thanksPageInfo}
                onClose={onClose}
              />
            </Onboarding.StepRenderer>
          </Onboarding.Modal>
        </Onboarding.Provider>
      </ErrorBoundary>
    </>
  )
}

export default SimilacOnboardingModal
